import { useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Chip, Panel } from "@/components/common/Chrome";
import { money } from "@/lib/format";

const DAY = 86400000;
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const hhmm = (value) => String(value || "").slice(11, 16);

/** A week grid of every room, so a clash is obvious before anyone tries to book one. */
export const BookingCalendar = ({ spaces, bookings, requests = [], onOpen, onOpenRequest }) => {
  const [offset, setOffset] = useState(0);
  const week = useMemo(() => {
    const start = startOfWeek(new Date(Date.now() + offset * 7 * DAY));
    return [...Array(7)].map((_, i) => new Date(start.getTime() + i * DAY));
  }, [offset]);

  const live = bookings.filter((b) => b.status !== "cancelled");
  const cellFor = (spaceCode, day) => live.filter((b) => {
    const from = String(b.starts_at || "").slice(0, 10);
    const to = String(b.ends_at || b.starts_at || "").slice(0, 10);
    return b.space_code === spaceCode && from <= iso(day) && iso(day) <= to;
  });
  const label = `${week[0].toLocaleDateString(undefined, { day: "numeric", month: "short" })} — `
    + `${week[6].toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  const todayIso = iso(new Date());
  const pending = requests.filter((r) => r.status === "pending");
  const inWeek = live.filter((b) => week.some((d) => cellFor(b.space_code, d)
    .some((x) => x.booking_ref === b.booking_ref)));

  return (
    <Panel testId="panel-booking-calendar" kicker="Week view"
           title={`Room diary · ${label}`}
           actions={(
             <div className="flex items-center gap-1.5">
               <Chip className="border-border bg-muted text-muted-foreground">
                 {inWeek.length} booking{inWeek.length === 1 ? "" : "s"} ·{" "}
                 {money(inWeek.reduce((t, b) => t + (b.charge || 0), 0), "INR", true)}
               </Chip>
               <Button size="sm" variant="outline" data-testid="calendar-prev-week"
                       onClick={() => setOffset(offset - 1)}
                       className="h-7 rounded-full border-border px-2">
                 <ChevronLeft className="h-3.5 w-3.5" />
               </Button>
               <Button size="sm" variant="outline" data-testid="calendar-this-week"
                       onClick={() => setOffset(0)}
                       className="h-7 rounded-full border-border px-2.5 text-[10px]">
                 This week
               </Button>
               <Button size="sm" variant="outline" data-testid="calendar-next-week"
                       onClick={() => setOffset(offset + 1)}
                       className="h-7 rounded-full border-border px-2">
                 <ChevronRight className="h-3.5 w-3.5" />
               </Button>
             </div>
           )}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse" data-testid="booking-calendar-grid">
          <thead>
            <tr>
              <th className="w-[190px] border-b border-r border-border px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                Room
              </th>
              {week.map((d, i) => (
                <th key={iso(d)}
                    className={`border-b border-border px-2 py-2 text-left font-mono text-[10px] uppercase tracking-[0.14em] ${
                      iso(d) === todayIso ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}>
                  {DAY_NAMES[i]} {d.getDate()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {spaces.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-[11.5px] text-muted-foreground"
                      data-testid="calendar-no-rooms">
                Add a room or hall first and it will appear here.
              </td></tr>
            )}
            {spaces.map((s) => (
              <tr key={s.code} data-testid={`calendar-row-${s.code}`}>
                <td className="border-b border-r border-border px-3 py-2 align-top">
                  <div className="text-[11.5px] font-medium text-foreground">{s.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {s.room_number || s.code} · seats {s.capacity}
                  </div>
                </td>
                {week.map((d) => {
                  const cell = cellFor(s.code, d);
                  const asks = pending.filter(
                    (r) => (r.space_code === s.code || !r.space_code)
                      && String(r.starts_at || "").slice(0, 10) === iso(d));
                  return (
                    <td key={iso(d)}
                        className={`border-b border-border px-1.5 py-1.5 align-top ${
                          iso(d) === todayIso ? "bg-primary/10 dark:bg-primary/[0.14]" : ""}`}>
                      <div className="space-y-1">
                        {cell.map((b) => (
                          <button key={b.booking_ref} type="button"
                                  onClick={() => onOpen?.(b)}
                                  data-testid={`calendar-booking-${b.booking_ref}`}
                                  className={`w-full rounded-md border px-1.5 py-1 text-left transition-colors duration-150 hover:border-primary/60 ${
                                    b.booking_type === "internal"
                                      ? "border-border bg-muted text-foreground/80"
                                      : "border-primary/40 bg-primary/10 text-foreground"}`}>
                            <div className="truncate text-[10.5px] font-medium leading-tight">
                              {b.title}
                            </div>
                            <div className="font-mono text-[9px] text-muted-foreground">
                              {hhmm(b.starts_at)}–{hhmm(b.ends_at)}
                              {b.charge > 0 ? ` · ${money(b.charge, b.currency, true)}` : " · free"}
                            </div>
                          </button>
                        ))}
                        {asks.map((r) => (
                          <button key={r.request_ref} type="button"
                                  onClick={() => onOpenRequest?.(r)}
                                  data-testid={`calendar-request-${r.request_ref}`}
                                  className="w-full rounded-md border border-dashed border-amber-500/70 bg-amber-500/15 px-1.5 py-1 text-left transition-colors duration-150 hover:bg-amber-500/25">
                            <div className="truncate text-[10.5px] font-medium leading-tight text-amber-900 dark:text-amber-100">
                              {r.purpose || r.organisation || r.requester_name}
                            </div>
                            <div className="font-mono text-[9px] text-amber-800/80 dark:text-amber-200/80">
                              {hhmm(r.starts_at)}–{hhmm(r.ends_at)} · awaiting approval
                            </div>
                          </button>
                        ))}
                        {cell.length > 1 && (
                          <div className="rounded border border-amber-500/50 bg-amber-500/25 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800 dark:text-amber-100"
                               data-testid={`calendar-busy-${s.code}-${iso(d)}`}>
                            {cell.length} bookings
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
        <CalendarDays className="h-3.5 w-3.5 text-primary" />
        Blue blocks are chargeable external hires, grey are internal use. Dashed amber blocks are
        public requests awaiting approval. Click any block to open it. A new booking that overlaps a
        confirmed booking is refused.
      </div>
    </Panel>
  );
};
